import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { IntegrationsApiService } from './integrations-api.service';
import { IntegrationState } from './integrations.model';

/**
 * Business-scoped cache of the authoritative Meta integration state
 * (`GET /integrations/state`). One shared read for Settings and every App entry
 * point so they never disagree. Must be cleared on business switch, logout and
 * App install/uninstall, and refreshed after OAuth / grant lifecycle actions.
 */
@Injectable({ providedIn: 'root' })
export class IntegrationStateService {
  private cache: { businessId: string; state: IntegrationState } | null = null;
  private inflight: { businessId: string; request: Observable<IntegrationState> } | null = null;

  private invalidatedSubject = new Subject<void>();
  readonly invalidated$: Observable<void> = this.invalidatedSubject.asObservable();

  constructor(private api: IntegrationsApiService) { }

  /** Cached state for the active business; fetches when empty, stale-by-business or `force`d. */
  get(force = false): Observable<IntegrationState> {
    const businessId = localStorage.getItem('businessId') || '';
    // Keyed by business AND signed-in staff, so a logout/login as someone else
    // (sign-out wipes localStorage) can never be served the previous user's state.
    const scope = `${businessId}|${localStorage.getItem('staffId') || ''}`;
    if (!force && this.cache?.businessId === scope) {
      return of(this.cache.state);
    }
    if (this.inflight?.businessId === scope && !force) {
      return this.inflight.request;
    }
    const request = this.api.getState(businessId).pipe(
      map(res => res?.data || { installedApps: [], resources: [] }),
      tap(state => { this.cache = { businessId: scope, state }; }),
      finalize(() => { if (this.inflight?.request === request) { this.inflight = null; } }),
      shareReplay(1)
    );
    this.inflight = { businessId: scope, request };
    return request;
  }

  /** Always hit the server (after OAuth, account selection, provision, enable/disable). */
  refresh(): Observable<IntegrationState> {
    return this.get(true);
  }

  /** Drop everything business-scoped — business switch, logout. Silent: nothing reloads. */
  clear(): void {
    this.cache = null;
    this.inflight = null;
  }

  /**
   * Drop the cache AND tell mounted screens (Settings hub, Ads shell) to reload —
   * used after App install/uninstall, where the backend reconciles grants on its own.
   */
  invalidate(): void {
    this.clear();
    this.invalidatedSubject.next();
  }
}
